/**
 * PremiumIMEIInput.jsx
 * Input de IMEI com validação Luhn
 *
 * Suporta:
 * - IMEI 15 dígitos
 * - Validação Luhn checksum
 * - Apenas números
 * - Opção para pular validação Luhn (alguns rastreadores usam IDs customizados)
 */

import React, { useState, useCallback, forwardRef } from 'react';
import { InputAdornment } from '@mui/material';
import RouterIcon from '@mui/icons-material/Router';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PremiumInput from './PremiumInput';

/**
 * Validação Luhn (Mod 10)
 * Usado para validar IMEI, cartões de crédito, etc.
 */
const luhnCheck = (imei) => {
  if (!/^\d{15}$/.test(imei)) return false;

  let sum = 0;
  for (let i = 0; i < 15; i++) {
    let digit = parseInt(imei[i], 10);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
};

/**
 * Formata o IMEI (apenas dígitos)
 */
const formatIMEI = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '').slice(0, 15);
};

/**
 * Valida o IMEI
 */
const validateIMEI = (value, skipLuhn = false) => {
  if (!value) return { valid: null, message: '' };

  const clean = value.replace(/\D/g, '');

  if (clean.length === 0) {
    return { valid: null, message: '' };
  }

  if (clean.length < 15) {
    return { valid: null, message: `${clean.length}/15 dígitos` };
  }

  if (clean.length === 15) {
    if (skipLuhn) {
      return { valid: true, message: 'IMEI válido' };
    }

    if (luhnCheck(clean)) {
      return { valid: true, message: 'IMEI válido (Luhn OK)' };
    }

    return { valid: false, message: 'IMEI inválido (checksum)' };
  }

  return { valid: false, message: 'IMEI deve ter 15 dígitos' };
};

/**
 * PremiumIMEIInput Component
 *
 * @param {boolean} skipLuhnValidation - Pular validação Luhn (para IDs customizados)
 */
const PremiumIMEIInput = forwardRef(({
  value,
  onChange,
  label = 'IMEI',
  skipLuhnValidation = false,
  showIcon = true,
  ...props
}, ref) => {
  const [validation, setValidation] = useState({ valid: null, message: '' });

  const handleChange = useCallback((e) => {
    const formatted = formatIMEI(e.target.value);
    const result = validateIMEI(formatted, skipLuhnValidation);
    setValidation(result);
    onChange?.({ target: { value: formatted, name: e.target.name } });
  }, [onChange, skipLuhnValidation]);

  const getHelperTextColor = () => {
    if (validation.valid === true) return 'success.main';
    if (validation.valid === false) return 'error.main';
    return 'text.secondary';
  };

  return (
    <PremiumInput
      ref={ref}
      {...props}
      value={value || ''}
      onChange={handleChange}
      label={label}
      placeholder="000000000000000"
      inputProps={{
        maxLength: 15,
        inputMode: 'numeric',
        pattern: '[0-9]*',
        style: { letterSpacing: '0.1em', fontFamily: 'monospace' },
        ...props.inputProps,
      }}
      InputProps={{
        startAdornment: showIcon ? (
          <InputAdornment position="start">
            <RouterIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          </InputAdornment>
        ) : undefined,
        endAdornment: validation.valid === true ? (
          <InputAdornment position="end">
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
          </InputAdornment>
        ) : undefined,
        ...props.InputProps,
      }}
      helperText={validation.message}
      FormHelperTextProps={{
        sx: { color: getHelperTextColor() },
      }}
      error={validation.valid === false}
      success={validation.valid === true}
    />
  );
});

PremiumIMEIInput.displayName = 'PremiumIMEIInput';

export default PremiumIMEIInput;
